import {
  DialogContent as ReachDialogContent,
  DialogOverlay as ReachDialogOverlay,
} from "@reach/dialog";
import {
  Box,
  Flex,
  LegacyText,
  PrimaryButton,
  Text,
  TransparentButton,
} from "flicket-ui";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import styled from "styled-components";
import { Url } from "url";
import { Icon } from "~components";
import { OrderQuery, OrderStatus } from "~graphql/sdk";
import { useOrganization } from "~hooks";
import { useTimer } from "~hooks/checkout/useTimer";
import { getUrl } from "~lib";

const MotionOverlay = motion(ReachDialogOverlay);
const MotionDialog = motion(ReachDialogContent);

const DialogOverlay = styled(MotionOverlay)<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 100%;
  min-width: 100%;
  max-width: 100%;
  min-height: 100%;

  z-index: ${(p) => p.theme.zIndices.popover + 2};

  && {
    background: rgba(0, 0, 0, 0.6);
  }
`;

const DialogContent = styled(MotionDialog)`
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;

  height: 100%;
  min-height: 128px;
  width: 75%;
  max-width: 428px;

  margin: 0 auto;

  padding: 24px;

  background: ${(p) => p.theme.colors.white};

  box-shadow: ${(p) => p.theme.shadows.sm};

  border-radius: 12px;

  z-index: ${(p) => p.theme.zIndices.popover + 1};

  && {
    background: white;
  }

  @media (max-width: ${(p) => p.theme.breakpoints.sm}) {
    width: 100%;
    padding: 16px;
  }

  @media (min-width: ${(p) => p.theme.breakpoints.lg}) {
    width: 55%;
  }
`;

const displayDuration = (durationInSeconds: number) => {
  const minutes = Math.floor(durationInSeconds / 60);
  const seconds = durationInSeconds - Math.floor(durationInSeconds / 60) * 60;

  let displayMinutes = `${minutes}`;
  let displaySeconds = `${seconds}`;

  if (minutes === 0 || minutes < 10) {
    displayMinutes = `0${minutes}`;
  }

  if (seconds === 0 || seconds < 10) {
    displaySeconds = `0${seconds}`;
  }

  return `${displayMinutes}:${displaySeconds}`;
};

interface TimerOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  isGeneralAdmissionEvent?: boolean;
}

const TimerOverlay = ({
  isOpen,
  onClose,
  isGeneralAdmissionEvent,
}: TimerOverlayProps) => {
  return (
    <DialogOverlay
      isOpen={isOpen}
      onDismiss={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      aria-label="dialog-overlay"
    >
      <DialogContent
        aria-label="dialog-content"
        initial={{ y: "-100%" }}
        animate={{ y: 0 }}
        exit={{ y: "-100%" }}
        role="modal"
      >
        <Text variant="header.S" textAlign="center" mb={1}>
          {"Whoops, looks like you've taken too long!"}
        </Text>
        <Text variant="regular" mb={3} textAlign="center">
          In order to make sure the{" "}
          {isGeneralAdmissionEvent ? "tickets" : "seats"} you want are still
          available you need to select them again.
        </Text>
        <PrimaryButton onClick={onClose}>
          Go back to ticket selection
        </PrimaryButton>
      </DialogContent>
    </DialogOverlay>
  );
};

const Tooltip = () => (
  <Box
    position="absolute"
    top={36}
    right={0}
    bg="N100"
    borderRadius="sm"
    py="1/2"
    px={1}
    zIndex="tooltip"
  >
    <LegacyText>Duration the tickets are held</LegacyText>
  </Box>
);

export interface TimerProps {
  order?: OrderQuery["order"];
  isGeneralAdmissionEvent?: boolean;
  redirectPath?: string | Partial<Url>;
  children?: (props: {
    secondsRemaining: string;
    seconds: number;
  }) => React.ReactNode;
}

export const PaymentTimer = ({ order, ...props }: TimerProps) => {
  {
    return order ? (
      <Box
        mt={2}
        w="400px"
        marginX={"auto" as any}
        d={{ _: "block", md: "none" }}
      >
        <Timer {...props} order={order} />
      </Box>
    ) : null;
  }
};

export const PreAuthTimer = ({ order, ...props }: TimerProps) => {
  if (!order) {
    return null;
  }

  return (
    <Timer {...props} order={order}>
      {({ secondsRemaining }) => (
        <Flex
          alignItems="center"
          px={2}
          py="1/2"
          bg="N100"
          borderRadius="xs"
          mb={{ _: "6/4", md: 0 }}
          maxW="fit-content"
          mr={{ _: 0, md: 2 }}
        >
          <Text
            variant="regular"
            color={"error"}
            fontWeight={"demiBold"}
            width={"115px"}
            textAlign="center"
          >
            Time left {secondsRemaining}
          </Text>
        </Flex>
      )}
    </Timer>
  );
};

export const Timer = ({
  order,
  isGeneralAdmissionEvent = false,
  redirectPath: redirectPathProp,
  children,
}: TimerProps & { order: OrderQuery["order"] | undefined }) => {
  const router = useRouter();
  const { organization } = useOrganization();

  const [showOverlay, setShowOverlay] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const expiresInSeconds = useMemo(
    () => ({ value: order?.expiresInSeconds - 60 }), // Subtract 1 minute to reduce the risk of the timer expiring before the payment is completed
    [order]
  );

  const timer = useTimer({
    expiresInSeconds,
    onExpiry: () => {
      if (
        ![OrderStatus.Paid, OrderStatus.Hold, OrderStatus.Completed].includes(
          order.status
        )
      ) {
        setShowOverlay(true);
      }
    },
  });

  if (!order) return null;

  let redirectPath =
    redirectPathProp ??
    (order.membership
      ? `/memberships/${order.membership?.id}/reservation`
      : `/events/${order.event?.id}/reservation`);

  if (order.pos) {
    redirectPath = `${getUrl(organization.slug)}/pos/${order.pos.id}`;
  }

  // Hide the timer until only an hour remains (only really relevant for transfer orders, which are allowed
  // to be pending for 3 days)
  if (timer.secondsRemaining > 60 * 60) {
    return null;
  }

  const secondsRemaining =
    timer.secondsRemaining > 0
      ? displayDuration(timer.secondsRemaining) || "-"
      : timer.expired
      ? "00:00"
      : "--:--";

  return (
    <Flex position="relative" justifyContent="center">
      {typeof children === "function" ? (
        children({ secondsRemaining, seconds: timer.secondsRemaining })
      ) : (
        <Flex
          alignItems="center"
          px={2}
          py="1/2"
          bg="N100"
          borderRadius="xs"
          mb={{ _: "6/4", md: 0 }}
          maxW="fit-content"
          mr={{ _: 0, md: 2 }}
        >
          <Text variant="regular" color="N800" whiteSpace="nowrap">
            Time left to complete this step
          </Text>
          <LegacyText
            lineHeight="high"
            fontWeight="heavy"
            ml="1/2"
            mr={1}
            width="30px"
            fontSize={2}
            mb={"-2px" as any}
          >
            {secondsRemaining}
          </LegacyText>
          <TransparentButton
            onClick={() => setShowTooltip(!showTooltip)}
            onMouseOver={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            d={{ _: "none", md: "inherit" }}
          >
            <Icon icon="help" color="P300" fontSize={3} />
          </TransparentButton>
        </Flex>
      )}
      <AnimatePresence>
        {showOverlay && (
          <TimerOverlay
            onClose={() => void router.push(redirectPath)}
            isOpen={showOverlay}
            isGeneralAdmissionEvent={isGeneralAdmissionEvent}
          />
        )}
      </AnimatePresence>
      {showTooltip && <Tooltip />}
    </Flex>
  );
};
